/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useFocus } from '../hooks/UNSAFE_useFocus';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { useId } from '../hooks/UNSAFE_useId';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { useCheckboxRadioContext } from '../PRIVATE_CheckboxRadioField/CheckboxRadioContext';
import { CheckIcon } from '../PRIVATE_ThemedIcons/CheckIcon';
import { RadioOffIcon } from '../PRIVATE_ThemedIcons/RadioOffIcon';
import { RadioOnIcon } from '../PRIVATE_ThemedIcons/RadioOnIcon';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { useRadioSetContext } from '../UNSAFE_RadioSet/RadioSetContext';
import { BundleType } from '../resources/nls/bundle';

import { IconUserAssistance } from '../UNSAFE_UserAssistance';
import { radioIconVars } from './themes/RadioIconContract.css';
import { RadioIconRedwoodTheme } from './themes/redwood/RadioIconTheme';
import { RadioRedwoodTheme } from './themes/redwood/RadioTheme';
import { useRadioItemContext } from './RadioItemContext';

type Props = {
  /**
   * Text to provide guidance to help the user understand what data to enter.
   */
  assistiveText?: string;
  /**
   * Text that describes the radio button value.
   */
  children: string;
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: string;
  /**
   * Custom text to be rendered for the `helpSourceLink`.
   */
  helpSourceText?: string;
  /**
   * Value of the radio button.
   */
  value: string | number;
};

export const RadioItem = ({ value, children, ...assistanceProps }: Props) => {
  const { isReadonly: isFormReadonly, isDisabled: isFormDisabled } = useFormContext();
  const { isDisabled: isFieldDisabled, isReadonly: isFieldReadonly } = useFormFieldContext();
  const { name, value: groupValue, onCommit } = useRadioSetContext();
  const { isFocusRingShown } = useCheckboxRadioContext();
  const { index } = useRadioItemContext();
  const { isTabbable } = useTabbableMode();

  const id = useId();
  const { isFocus, focusProps } = useFocus();

  const isSelected = groupValue === value;
  const isDisabled = isFieldDisabled ?? isFormDisabled;
  const isReadonly = isFieldReadonly ?? isFormReadonly;

  const { interactionProps, applyActiveStyle } = useInteractionStyle();

  const {
    classes: radioClasses,
    styles: { valueLabelStyle }
  } = useComponentTheme(RadioRedwoodTheme, {
    disabled: isDisabled ? 'isDisabled' : 'notDisabled',
    focused: isFocus && isFocusRingShown ? 'isFocused' : ''
  });

  const { classes: radioIconClasses } = useComponentTheme(RadioIconRedwoodTheme, {
    disabled: isDisabled ? 'isDisabled' : 'notDisabled',
    selected: isSelected ? 'isSelected' : 'notSelected',
    readonly: isReadonly ? 'isReadonly' : 'notReadonly',
    active: applyActiveStyle ? 'isActive' : 'notActive'
  });

  const { assistiveText, helpSourceLink } = assistanceProps;
  const hasUserAssistive = !isDisabled && !isReadonly && !!(assistiveText || helpSourceLink);
  const helpAvailableId = hasUserAssistive ? `${id}-help-available` : undefined;
  const uaId = hasUserAssistive ? `${id}-icon-ua` : undefined;

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const helpAvailableString = translations.formControl_helpAvailable();

  const IconComp = isReadonly
    ? isSelected
      ? CheckIcon
      : RadioOffIcon
    : isSelected
    ? RadioOnIcon
    : RadioOffIcon;

  const tabIndex = isTabbable !== false && ((index === 0 && !groupValue) || isSelected) ? 0 : -1;

  return (
    <div>
      <label class={radioClasses}>
        <HiddenAccessible>
          <input
            type="radio"
            name={name}
            id={id}
            value={value}
            {...(!isReadonly &&
              !isDisabled && {
                onChange: (e) => {
                  e.stopPropagation();
                  onCommit?.({ previousValue: groupValue, value });
                }
              })}
            checked={isSelected}
            disabled={isDisabled}
            aria-describedby={helpAvailableId}
            tabIndex={tabIndex}
            {...focusProps}
          />
          {hasUserAssistive && (
            <HiddenAccessible id={helpAvailableId} isHidden={true}>
              {helpAvailableString}
            </HiddenAccessible>
          )}
        </HiddenAccessible>
        <span aria-hidden="true" class={radioIconClasses} {...interactionProps}>
          <IconComp size={radioIconVars.iconSize} color="currentColor" />
        </span>
        <span class={valueLabelStyle}>
          <span {...interactionProps}>{children}</span>
          {hasUserAssistive && (
            <IconUserAssistance
              {...assistanceProps}
              isTabbable={isTabbable && isFocus}
              id={uaId as string}
            />
          )}
        </span>
      </label>
    </div>
  );
};
